
import { useMemo } from "react";
import { ArrowUpCircle, ArrowDownCircle, TrendingUp } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Transaction } from "@/types/transaction";
import { formatCurrency } from '@/lib/utils/currencyUtils';
import { useTransactions } from "@/hooks/useTransactions";

interface FinancialSummaryCardsProps {
  transactions: Transaction[] | undefined;
  dateRange?: { from?: Date; to?: Date };
}

export function FinancialSummaryCards({ transactions, dateRange }: FinancialSummaryCardsProps) {
  const { transactions: historicalTransactions } = useTransactions({ type: 'expense' });
  
  // Calculate dynamic expense target based on historical data
  const calculateDynamicTarget = useMemo(() => {
    if (!historicalTransactions || historicalTransactions.length === 0) {
      return 0; // No historical data, no target
    }
    
    // Get expenses from last 3 months for comparison
    const threeMonthsAgo = new Date();
    threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
    
    const recentExpenses = historicalTransactions.filter(transaction => {
      const transactionDate = new Date(transaction.date);
      return transaction.type === 'expense' && transactionDate >= threeMonthsAgo;
    });
    
    if (recentExpenses.length === 0) {
      return 0;
    }
    
    // Calculate average monthly expense
    const totalExpenses = recentExpenses.reduce((sum, t) => sum + Number(t.amount), 0);
    const monthlyAverage = totalExpenses / 3; // Average over 3 months
    
    // Add 10% buffer for realistic target
    return Math.round(monthlyAverage * 1.1);
  }, [historicalTransactions]);

  const summary = useMemo(() => {
    if (!transactions) {
      return {
        income: { paid: 0, total: 0 },
        expense: { paid: 0, total: 0, target: calculateDynamicTarget }
      };
    }
    
    const currentTransactions = transactions.reduce((acc, transaction) => {
      if (transaction.type === "income") {
        acc.income.total += Number(transaction.amount);
        if (transaction.is_paid) {
          acc.income.paid += Number(transaction.amount);
        }
      } else if (transaction.type === "expense") {
        acc.expense.total += Number(transaction.amount);
        if (transaction.is_paid) {
          acc.expense.paid += Number(transaction.amount);
        }
      }
      return acc;
    }, {
      income: { paid: 0, total: 0 },
      expense: { paid: 0, total: 0, target: calculateDynamicTarget }
    });

    return currentTransactions;
  }, [transactions, calculateDynamicTarget]);


  const calculateProgress = (paid: number, total: number) => {
    if (total === 0) return 0;
    return Math.min(Math.round((paid / total) * 100), 100);
  };

  const calculateExpenseProgress = (current: number, target: number) => {
    if (target === 0) return 0;
    return Math.min(Math.round((current / target) * 100), 100);
  };

  const incomeProgress = calculateProgress(summary.income.paid, summary.income.total);
  const expenseProgress = calculateExpenseProgress(summary.expense.total, summary.expense.target);

  return (
    <div className="grid grid-cols-2 gap-4 mb-4">
      <Card className="p-6">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-base font-medium text-muted-foreground">Receitas</h3>
          <ArrowUpCircle className="h-5 w-5 text-finance-green" />
        </div>
        <p className="text-2xl font-bold">{formatCurrency(summary.income.paid)}</p>
        <div className="mt-4">
          <Progress 
            value={incomeProgress} 
            className="h-2 bg-finance-green/20"
          />
          <div className="mt-1 flex justify-between text-sm">
            <span className="text-muted-foreground">{incomeProgress}%</span>
            <span className="text-muted-foreground">{formatCurrency(summary.income.total)}</span>
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-base font-medium text-muted-foreground">Despesas</h3>
          <ArrowDownCircle className="h-5 w-5 text-finance-red" />
        </div>
        <p className="text-2xl font-bold">{formatCurrency(summary.expense.total)}</p>
        <div className="mt-4">
          {summary.expense.target > 0 ? (
            <>
              <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
                <span>Meta: {formatCurrency(summary.expense.target)}</span>
                <span className="flex items-center gap-1">
                  <TrendingUp className="h-3 w-3" />
                  Baseado no histórico
                </span>
              </div>
              <Progress 
                value={expenseProgress} 
                className={`h-2 ${
                  expenseProgress > 100 
                    ? 'bg-red-100' 
                    : expenseProgress > 80 
                    ? 'bg-yellow-100' 
                    : 'bg-finance-red/20'
                }`}
              />
              <div className="mt-1 flex justify-between text-sm">
                <span className="text-muted-foreground">{expenseProgress}% da meta</span>
                <span className={
                  expenseProgress > 100 
                    ? 'text-red-600 font-medium' 
                    : expenseProgress > 80 
                    ? 'text-yellow-600 font-medium' 
                    : 'text-muted-foreground'
                }>
                  {formatCurrency(summary.expense.total)} / {formatCurrency(summary.expense.target)}
                </span>
              </div>
            </>
          ) : (
            <div className="text-sm text-muted-foreground">
              Sem dados históricos para calcular meta
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
